import pluralize from 'pluralize';
import React, { useEffect, useRef, useState } from 'react';
import SelectAllStudies from '../SelectAllStudies';
import { ApplyTagsFunction, TagsAppliedFunction } from '../StudyList';
import {
  SimplifiedStudy,
  SortAndFilter,
  StudyListFilter,
  StudyListStage,
} from '../types';
import * as styles from './StudyListToolbar.module.scss';
import { useFilterSortDisplayForm } from './components/useFilterSortDisplayForm';
import { StartDiscussionsButton } from './components/StartDiscussionsButton';
import UndoVote from './components/UndoVote';
import { MergeStudiesButton } from './components/MergeStudiesButton';
import { SidebarPanel, SidebarName } from './components/SidebarPanel';
import {
  TAGS_SIDEBAR_ID,
  dispatchSidebarChangeEvent,
  useOpenSidebarRequested,
} from './sidebarEvents';
import { useCriteriaCache } from 'hooks/reviews/useCriteriaCache';
import { useCriteria } from 'hooks/reviews/useCriteria';
import { Badge, Button, Icon, IconList, Text } from 'components/core';
import { EditableCriteriaHandle } from 'components/shared/EditableCriteria';
import { Inline } from 'components/layout';
import { getReviewId } from 'query/review';
import { HighlightsResult } from 'query/review/Highlights';
import { addTagsToStudies } from 'query/review/Tags';
import { useOnHighlightsSaved } from 'hooks/reviews/useHighlights';
import { useScrollPadding } from 'hooks/useScrollPadding';
import { QueryProvider } from 'query/QueryProvider';
import { ReviewFeaturesProvider } from 'contexts/ReviewFeaturesProvider';
import { useUniqueID } from 'hooks/useAccessibleName';
import { useStudyListCookieToasts } from 'pages/StudyList/useStudyListCookieToasts';

export type ToolbarOptions = {
  isHighlightsSidebarEnabled: boolean;
  isDiscussionsEnabled: boolean;
  isMergeStudiesEnabled: boolean;
  isCriteriaEnabled: boolean;
  reviewStage: StudyListStage;
};

type Props = {
  // Toolbar props
  toolbarOptions: ToolbarOptions;
  // Select all props
  handleSelectAll: (checked: boolean) => void;
  // 'Merge as study' props
  onMergeSuccess?: () => void;
  // Discussions props
  selectedStudyIds?: number[];
  studies?: SimplifiedStudy[];
  // Sort & Filter sidebar props
  allowAISort?: boolean;
  sortAndFilter: SortAndFilter;
  // Criteria props
  hasCriteria: boolean;
  // Highlights sidebar props
  defaultShowAbstracts: boolean;
  hasHighlights: boolean;
  toggleAbstracts: () => void;
  toggleHighlights: () => void;
  enableHighlights: () => void;
  onHighlightsChange: (highlights: HighlightsResult) => void;
  reviewId?: string;
  // Undo votes
  handleUndo?: () => Promise<any>;
  handleSkip?: () => Promise<any>;
  previouslyVotedStudyTitle?: any;
  // Saves the tags. Reject to keep the tags sidebar open for a retry
  onApplyTags: ApplyTagsFunction;
  // Called once the tags sidebar has closed, to update the page
  onTagsApplied: TagsAppliedFunction;
  listFilter: StudyListFilter;
  areAllStudiesSelected: boolean;
};

export const StudyListToolbar = ({
  toolbarOptions,
  handleSelectAll,
  onMergeSuccess,
  selectedStudyIds = [],
  studies = [],
  allowAISort = false,
  sortAndFilter,
  hasCriteria: hasCriteriaAtLoad,
  defaultShowAbstracts,
  hasHighlights,
  toggleAbstracts,
  toggleHighlights,
  enableHighlights,
  onHighlightsChange,
  reviewId,
  handleUndo,
  handleSkip,
  previouslyVotedStudyTitle,
  onApplyTags,
  onTagsApplied,
  listFilter,
  areAllStudiesSelected,
}: Props) => {
  useOnHighlightsSaved(onHighlightsChange);
  const isAwaitingOtherReviewer = listFilter === 'other_vote_required';
  const toolbarRef = useRef<HTMLDivElement>(null);
  const studiesSelectedCount = selectedStudyIds.length;
  const isMergeStudiesButtonDisabled = studiesSelectedCount < 2;

  const criteriaDisclosureId = useUniqueID({ postfix: 'criteria-disclosure' });
  const criteriaToggleRef = useRef<HTMLButtonElement>(null);
  const highlightsDisclosureId = useUniqueID({
    postfix: 'highlights-disclosure',
  });
  const highlightsToggleRef = useRef<HTMLButtonElement>(null);
  const sortAndFilterDisclosureId = useUniqueID({
    postfix: 'sort-and-filter-disclosure',
  });
  const sortAndFilterToggleRef = useRef<HTMLButtonElement>(null);
  const tagsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const editableCriteriaRef = useRef<EditableCriteriaHandle>(null);
  const criteriaCache = useCriteriaCache();

  // Tracks criteria added or cleared since page load; the sidebar fetches them.
  const fetched = useCriteria({ enabled: false });
  const hasCriteria = fetched.criteria
    ? fetched.hasCriteria
    : hasCriteriaAtLoad;

  const [currentSidebar, setCurrentSidebar] = useState<SidebarName | null>(
    null
  );
  const nextSidebar = useRef<SidebarName | null>(null);

  useOpenSidebarRequested(({ name, trigger }) => {
    if (name === 'tags') {
      tagsTriggerRef.current = trigger ?? null;
    }
    if (currentSidebar === name) return;

    openSidebar(name);
  });

  useEffect(() => {
    dispatchSidebarChangeEvent(currentSidebar);
  }, [currentSidebar]);

  const isCriteriaOpen = currentSidebar === 'criteria';
  const isHighlightsOpen = currentSidebar === 'highlights';
  const isSortAndFilterOpen = currentSidebar === 'sortAndFilter';
  const isTagsOpen = currentSidebar === 'tags';

  const sidebarTriggerProps = (name: SidebarName, disclosureId: string) => ({
    role: 'button',
    'aria-expanded': currentSidebar === name,
    'aria-controls': disclosureId,
  });

  // Located here so it renders when the list is and isn't populated
  useStudyListCookieToasts();

  useScrollPadding(toolbarRef);

  const {
    form: filterSortDisplayForm,
    sortOptions,
    isResetting,
    numStudiesFound,
    appliedFilterCount,
    applySortAndFilter,
    resetFiltersAndRefresh,
  } = useFilterSortDisplayForm({
    allowAISort,
    sortAndFilter,
  });
  const sortAndFilterLabel =
    appliedFilterCount > 0
      ? `Sort & Filter, ${pluralize('filter', appliedFilterCount, true)} active`
      : undefined;

  const requestCriteriaClose = () =>
    editableCriteriaRef.current?.requestClose();

  const closeCriteria = () => {
    nextSidebar.current = null;
    requestCriteriaClose();
  };

  const confirmCriteriaClose = () => {
    setCurrentSidebar(nextSidebar.current);
    nextSidebar.current = null;
  };

  const openSidebar = (name: SidebarName) => {
    if (isCriteriaOpen) {
      // Open new sidebar only once the criteria panel agrees to close.
      nextSidebar.current = name;
      requestCriteriaClose();
      return;
    }

    setCurrentSidebar(name);
  };

  const closeSidebar = (name: Exclude<SidebarName, 'criteria'>) => {
    setCurrentSidebar((current) => (current === name ? null : current));
  };

  const toggleCriteriaPanel = () => {
    if (isCriteriaOpen) {
      closeCriteria();
    } else {
      // Force re-fetch on each open
      criteriaCache.invalidate();
      openSidebar('criteria');
    }
  };

  const criteriaTrigger = hasCriteria
    ? ({
        label: 'Eligibility criteria',
        icon: IconList.light.faListCheck,
        type: isCriteriaOpen ? 'primary' : 'neutral',
        variant: 'outline',
        pendoKey: 'show-criteria',
      } as const)
    : ({
        label: 'Add eligibility criteria',
        icon: IconList.light.faPlus,
        type: 'primary',
        variant: 'solid',
        pendoKey: 'add-criteria',
      } as const);

  const studyCountText = `(${studiesSelectedCount} ${pluralize(
    'study',
    studiesSelectedCount
  )})`;

  return (
    <div ref={toolbarRef}>
      <Inline
        alignX="spaceBetween"
        spacing={4}
        className={styles.StudyListToolbar}
      >
        <Inline alignX="left" spacing={2}>
          <SelectAllStudies
            isSelected={areAllStudiesSelected}
            handleStudySelection={(event) =>
              handleSelectAll(event.currentTarget.checked)
            }
            renderDSCheckbox={true}
            dataTrackKey="select-all"
          />
          {studiesSelectedCount > 0 && (
            <Button
              {...sidebarTriggerProps('tags', TAGS_SIDEBAR_ID)}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                if (isTagsOpen) {
                  closeSidebar('tags');
                  return;
                }
                // Need to set this manually here rather than on render, because
                // "Add tag" button in the study list also shares the trigger
                tagsTriggerRef.current = event.currentTarget;
                openSidebar('tags');
              }}
              type="brand"
              data-pendo-key={
                isTagsOpen ? 'close-tags-sidebar' : 'open-tags-sidebar'
              }
              className={styles.toggleSidebarButton}
            >
              <Icon icon={IconList.light.faTag} />
              Tag {studyCountText}
            </Button>
          )}
          {toolbarOptions.isMergeStudiesEnabled && (
            <MergeStudiesButton
              disabled={isMergeStudiesButtonDisabled}
              studies={studies}
              selectedStudyIds={selectedStudyIds}
              reviewId={reviewId || getReviewId() || ''}
              onMergeSuccess={onMergeSuccess}
            />
          )}
          {toolbarOptions.isDiscussionsEnabled && (
            <StartDiscussionsButton selectedStudyIds={selectedStudyIds} />
          )}
        </Inline>

        <Inline alignX="left" spacing={2}>
          <Button
            {...sidebarTriggerProps('sortAndFilter', sortAndFilterDisclosureId)}
            ref={sortAndFilterToggleRef}
            onClick={() =>
              isSortAndFilterOpen
                ? closeSidebar('sortAndFilter')
                : openSidebar('sortAndFilter')
            }
            type={isSortAndFilterOpen ? 'primary' : 'neutral'}
            variant="outline"
            data-pendo-key={
              isSortAndFilterOpen ? 'hide-sort-and-filter' : 'sort-and-filter'
            }
            className={styles.toggleSidebarButton}
            aria-label={sortAndFilterLabel}
          >
            <Icon icon={IconList.light.faArrowDownArrowUp} size="md" />
            Sort & Filter
            {appliedFilterCount > 0 && (
              <Badge type="primary" variant="solid">
                {appliedFilterCount}
              </Badge>
            )}
          </Button>
          {toolbarOptions.isCriteriaEnabled && (
            <Button
              {...sidebarTriggerProps('criteria', criteriaDisclosureId)}
              ref={criteriaToggleRef}
              onClick={toggleCriteriaPanel}
              type={criteriaTrigger.type}
              variant={criteriaTrigger.variant}
              data-pendo-key={
                isCriteriaOpen ? 'hide-criteria' : criteriaTrigger.pendoKey
              }
              className={styles.toggleSidebarButton}
            >
              <Icon icon={criteriaTrigger.icon} size="md" />
              {criteriaTrigger.label}
            </Button>
          )}
          {toolbarOptions.isHighlightsSidebarEnabled && (
            <Button
              {...sidebarTriggerProps('highlights', highlightsDisclosureId)}
              ref={highlightsToggleRef}
              onClick={() =>
                isHighlightsOpen
                  ? closeSidebar('highlights')
                  : openSidebar('highlights')
              }
              type={isHighlightsOpen || !hasHighlights ? 'primary' : 'neutral'}
              variant={isHighlightsOpen || hasHighlights ? 'outline' : 'solid'}
              data-pendo-key={
                hasHighlights ? 'edit-highlights' : 'add-highlights'
              }
              className={styles.toggleSidebarButton}
            >
              {hasHighlights ? (
                <>
                  <Icon icon={IconList.light.faHighlighter} size="md" />
                  Highlights
                </>
              ) : (
                <>
                  <Icon icon={IconList.light.faPlus} size="md" />
                  Add highlights
                </>
              )}
            </Button>
          )}
        </Inline>
        <div role="region" aria-live="polite" className={styles.ResultsRegion}>
          <UndoVote
            handleUndo={handleUndo}
            handleSkip={handleSkip}
            previouslyVotedStudyTitle={previouslyVotedStudyTitle}
          />

          {appliedFilterCount > 0 && (
            <>
              <div className={styles.ToolbarSeparator} />
              <Inline alignX="left" spacing={3}>
                <Text component="span">
                  {`${numStudiesFound} ${pluralize(
                    'study',
                    numStudiesFound
                  )} found`}
                </Text>
                <Button
                  onClick={resetFiltersAndRefresh}
                  isLoading={isResetting}
                  disabled={filterSortDisplayForm.formState.isSubmitting}
                  variant="outline"
                >
                  <Icon icon={IconList.light.faArrowRotateLeft} />
                  Reset filters
                </Button>
              </Inline>
            </>
          )}
        </div>
      </Inline>
      <SidebarPanel
        currentSidebar={currentSidebar}
        reviewId={reviewId}
        criteria={{
          editableCriteriaRef,
          disclosureId: criteriaDisclosureId,
          toggleRef: criteriaToggleRef,
          showEmptyState: !hasCriteria,
          close: closeCriteria,
          onCloseConfirmed: confirmCriteriaClose,
        }}
        highlights={{
          disclosureId: highlightsDisclosureId,
          toggleRef: highlightsToggleRef,
          defaultShowAbstracts,
          toggleAbstracts,
          toggleHighlights,
          enableHighlights,
        }}
        sortAndFilter={{
          disclosureId: sortAndFilterDisclosureId,
          toggleRef: sortAndFilterToggleRef,
          form: filterSortDisplayForm,
          sortOptions,
          isAwaitingOtherReviewer,
          reviewStage: toolbarOptions.reviewStage,
          isResetting,
          onApply: applySortAndFilter,
          onReset: resetFiltersAndRefresh,
        }}
        tags={{
          disclosureId: TAGS_SIDEBAR_ID,
          toggleRef: tagsTriggerRef,
          selectedStudyIds,
          onApplyTags,
          onTagsApplied,
        }}
        closeSidebar={closeSidebar}
        focusFallbackRef={toolbarRef}
      />
    </div>
  );
};

type HybridProps = Omit<
  Props,
  'onApplyTags' | 'onTagsApplied' | 'areAllStudiesSelected'
> & {
  onApplyTag: Props['onTagsApplied'];
  areAllStudiesSelected?: boolean;
};

// Backbone only syncs its models, so the tags are saved here first
const saveTags: Props['onApplyTags'] = async (reviewId, studyIds, tags) => {
  await addTagsToStudies(
    reviewId,
    studyIds.map((id) => id.toString()),
    tags
  );
};

/** @deprecated Only used by the Backbone study list, which has no providers above it. New pages use StudyListToolbar. */
export const HybridStudyListToolbar = ({
  onApplyTag,
  areAllStudiesSelected,
  handleSelectAll,
  sortAndFilter,
  ...props
}: HybridProps) => {
  // Backbone renders the toolbar before it clears its selection, so the
  // select all state is kept here instead of trusting areAllStudiesSelected
  const [allStudiesSelected, setAllStudiesSelected] = useState(false);

  useEffect(() => {
    if (
      // We don't want to set the state if isAllSelected is undefined
      areAllStudiesSelected != undefined &&
      areAllStudiesSelected != allStudiesSelected
    ) {
      setAllStudiesSelected(areAllStudiesSelected);
    }
  }, [areAllStudiesSelected]);

  const selectAll = (checked: boolean) => {
    setAllStudiesSelected(checked);
    handleSelectAll(checked);
  };

  // Backbone never sends false after a sort or filter, so clear it here
  const sortAndFilterThenDeselect: SortAndFilter = async (args) => {
    const studiesFound = await sortAndFilter(args);
    setAllStudiesSelected(false);
    return studiesFound;
  };

  return (
    <QueryProvider>
      <ReviewFeaturesProvider>
        <StudyListToolbar
          {...props}
          areAllStudiesSelected={allStudiesSelected}
          handleSelectAll={selectAll}
          sortAndFilter={sortAndFilterThenDeselect}
          onApplyTags={saveTags}
          onTagsApplied={onApplyTag}
        />
      </ReviewFeaturesProvider>
    </QueryProvider>
  );
};
